import type {
  CountryValue,
  DemographicDistribution,
  StateDistribution,
} from "@/types/resources";

export const equalWeights = (count: number): number[] => {
  const base = Math.floor(100 / count);
  const remainder = 100 - base * count;
  return Array.from(
    { length: count },
    (_, i) => (base + (i < remainder ? 1 : 0)) / 100
  );
};

export const isEquallyDistributed = (items: { weight: number }[]): boolean => {
  if (items.length <= 1) return true;
  const base = Math.floor(100 / items.length);
  return items.every(
    (d) =>
      Math.round(d.weight * 100) === base ||
      Math.round(d.weight * 100) === base + 1
  );
};

export const computeCountryDist = (
  nextCodes: CountryValue[],
  prev: DemographicDistribution<"country">[],
  items: { label: string; value: CountryValue }[]
): DemographicDistribution<"country">[] => {
  if (nextCodes.length === 0) return [];
  const wasEqual = isEquallyDistributed(prev);
  const added = nextCodes.find((c) => !prev.some((d) => d.value === c));
  const removed = prev.find((d) => !nextCodes.includes(d.value));
  if (wasEqual || prev.length === 0) {
    const weights = equalWeights(nextCodes.length);
    return nextCodes.map((code, i) => ({
      label:
        prev.find((d) => d.value === code)?.label ??
        items.find((it) => it.value === code)?.label ??
        code,
      value: code,
      weight: weights[i],
    }));
  }
  if (added) {
    const share = 0.01;
    const maxIdx = prev.reduce(
      (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
      0
    );
    return [
      ...prev.map((d, i) => ({
        ...d,
        weight: i === maxIdx ? d.weight - share : d.weight,
      })),
      {
        label: items.find((it) => it.value === added)?.label ?? added,
        value: added,
        weight: share,
      },
    ];
  }
  if (removed) {
    const kept = prev.filter((d) => nextCodes.includes(d.value));
    const freed = removed.weight;
    const maxIdx = kept.reduce(
      (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
      0
    );
    return kept.map((d, i) => ({
      ...d,
      weight: i === maxIdx ? d.weight + freed : d.weight,
    }));
  }
  return prev;
};

export const computeStateDist = (
  nextCodes: string[],
  prev: StateDistribution[],
  items: { label: string; value: string }[]
): StateDistribution[] => {
  if (nextCodes.length === 0) return [];
  const wasEqual = isEquallyDistributed(prev);
  const added = nextCodes.find((c) => !prev.some((d) => d.value === c));
  const removed = prev.find((d) => !nextCodes.includes(d.value));
  if (wasEqual || prev.length === 0) {
    const weights = equalWeights(nextCodes.length);
    return nextCodes.map((code, i) => ({
      label:
        prev.find((d) => d.value === code)?.label ??
        items.find((it) => it.value === code)?.label ??
        code,
      value: code,
      weight: weights[i],
    }));
  }
  if (added) {
    const share = 0.01;
    const maxIdx = prev.reduce(
      (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
      0
    );
    return [
      ...prev.map((d, i) => ({
        ...d,
        weight: i === maxIdx ? d.weight - share : d.weight,
      })),
      {
        label: items.find((it) => it.value === added)?.label ?? added,
        value: added,
        weight: share,
      },
    ];
  }
  if (removed) {
    const kept = prev.filter((d) => nextCodes.includes(d.value));
    const freed = removed.weight;
    const maxIdx = kept.reduce(
      (mi, d, i, a) => (d.weight > a[mi].weight ? i : mi),
      0
    );
    return kept.map((d, i) => ({
      ...d,
      weight: i === maxIdx ? d.weight + freed : d.weight,
    }));
  }
  return prev;
};
